<template>
  <UserShell
    title="MCN机构数字资产评估申请"
    subtitle="填写以下评估信息，系统将生成专业的资产评估报告。该报告可用于银行信贷、投融资等金融服务场景。"
    :profile="profile"
    :menu-items="menuItems"
    active-key="asset"
    center-path="/user/org"
  >
    <div class="notice-box">
      <img class="notice-icon" :src="tipIcon" alt="提示" />
      <div>
        <strong>资产评估报告的价值</strong>
        <p>MCN机构的核心资产在于旗下主播的粉丝规模、内容转化率以及机构的商业化流水能力。本评估报告将从主播孵化能力、内容影响力、账号资产积累、运营数据及商业化表现五大维度进行综合评估，为银行信贷审批提供权威数据支持。</p>
      </div>
    </div>

    <div v-if="rejectReason" class="reject-box">
      <span class="reject-label">驳回原因：</span>
      <span class="reject-text">{{ rejectReason }}</span>
    </div>

    <div class="stepper">
      <button
        v-for="step in steps"
        :key="step.no"
        type="button"
        :class="['step-item', stepState(step.no)]"
        @click="jumpToStep(step.no)"
      >
        <span class="step-no">{{ step.no }}</span>
        <span>{{ step.label }}</span>
      </button>
    </div>

    <section class="panel">
      <template v-if="currentStep === 1">
        <div class="panel-title">一、数字资产基本信息</div>
        <div class="panel-sub">填写MCN机构的基本工商信息及资产概况</div>

        <div class="form-grid top-grid">
          <div class="field-item wide"><label>MCN机构全称 <span class="required">*</span></label><el-input v-model.trim="form.companyName" placeholder="请输入机构工商注册全称" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item wide"><label>统一社会信用代码 <span class="required">*</span></label><el-input v-model.trim="form.creditCode" placeholder="18位统一社会信用代码" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item wide"><label>资产类别</label><el-select v-model="form.assetType" placeholder="请选择资产类别" class="control" :disabled="readonlyMode"><el-option label="机构资产" value="机构资产" /></el-select></div>
        </div>

        <div class="sub-section">权属人股权及融资信息</div>

        <div class="form-grid">
          <div class="field-item"><label>注册资本（万元） <span class="required">*</span></label><el-input v-model.trim="form.registerCapital" placeholder="请输入注册资本" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>实缴资本（万元） <span class="required">*</span></label><el-input v-model.trim="form.paidCapital" placeholder="请输入实缴资本" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="textarea-item"><label>股权结构 <span class="required">*</span> <span class="minor-tip">（持股比例、主要股东及实际控制人）</span></label><el-input v-model.trim="form.shareholders" type="textarea" :rows="3" resize="none" placeholder="请详细描述股权结构，例如：实际控制人张三持股60%，李四持股30%，员工持股平台持股10%" class="control textarea" :disabled="readonlyMode" /></div>
        <div class="textarea-item"><label>融资历史 <span class="minor-tip">（轮次、金额、投资方、估值）</span></label><el-input v-model.trim="form.financeHistory" type="textarea" :rows="3" resize="none" placeholder="例如：2023年A轮融资5000万元，投资方为XX资本，投后估值5亿元" class="control textarea" :disabled="readonlyMode" /></div>
        <div class="textarea-item"><label>控股子公司/关联公司 <span class="minor-tip">（与主营业务相关的关联主体）</span></label><el-input v-model.trim="form.relatedCompanies" type="textarea" :rows="3" resize="none" placeholder="列出与主播业务相关的控股子公司或关联公司" class="control textarea" :disabled="readonlyMode" /></div>

        <div class="sub-section">机构基本情况</div>

        <div class="form-grid">
          <div class="field-item"><label>成立时间 <span class="required">*</span></label><el-date-picker v-model="form.foundedAt" type="date" value-format="yyyy-MM-dd" placeholder="yyyy/mm/日" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>全职员工人数 <span class="required">*</span></label><el-input v-model.trim="form.staffCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>签约主播人数 <span class="required">*</span></label><el-input v-model.trim="form.anchorCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="chips-block">
          <label>主营业务范围 <span class="required">*</span></label>
          <el-checkbox-group v-model="form.businessScopes" class="chips-group" :disabled="readonlyMode">
            <el-checkbox v-for="item in businessScopeOptions" :key="item.value" :label="item.value">{{ item.label }}</el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="chips-block">
          <label>核心合作平台 <span class="required">*</span></label>
          <el-checkbox-group v-model="form.corePlatforms" class="chips-group" :disabled="readonlyMode">
            <el-checkbox v-for="item in corePlatformOptions" :key="item.value" :label="item.value">{{ item.label }}</el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="chips-block">
          <label>经营资质</label>
          <el-checkbox-group v-model="form.certificates" class="chips-group" :disabled="readonlyMode">
            <el-checkbox v-for="item in certificateOptions" :key="item.value" :label="item.value">{{ item.label }}</el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="form-grid bottom-grid">
          <div class="field-item wide"><label>机构办公地址 <span class="required">*</span></label><el-cascader v-model="officeRegion" :options="regionOptions" placeholder="请选择省市" class="control" :disabled="readonlyMode" @change="syncOfficeRegion" /></div>
          <div class="field-item wide"><label>行业荣誉认证 <span class="minor-tip">（MCN等级、行业奖项）</span></label><el-input v-model.trim="form.honors" placeholder="如：抖音金牌MCN、快手年度影响力机构" class="control" :disabled="readonlyMode" /></div>
        </div>
      </template>

      <template v-if="currentStep === 2">
        <div class="panel-title">二、主播孵化数据情况</div>
        <div class="panel-sub">填写MCN机构旗下主播的培养与梯队建设信息</div>

        <div class="sub-section">主播签约结构</div>
        <div class="form-grid three-grid">
          <div class="field-item"><label>签约主播总数 <span class="required">*</span></label><el-input v-model.trim="form.step2.signedAnchorCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>独家签约主播 <span class="required">*</span></label><el-input v-model.trim="form.step2.exclusiveAnchorCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item contract-group">
            <label>合同类型分布</label>
            <div class="dual-inputs">
              <el-input v-model.trim="form.step2.fullTimeContractCount" placeholder="全约人" class="control" :disabled="readonlyMode" />
              <el-input v-model.trim="form.step2.businessContractCount" placeholder="商务约人" class="control" :disabled="readonlyMode" />
            </div>
          </div>
        </div>

        <div class="sub-section">主播梯队结构</div>
        <div class="form-grid three-grid">
          <div class="field-item"><label>头部主播 <span class="minor-tip">（粉丝≥500万）</span></label><el-input v-model.trim="form.step2.topAnchorCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>腰部主播 <span class="minor-tip">（100万-500万）</span></label><el-input v-model.trim="form.step2.midAnchorCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>成长型主播 <span class="minor-tip">（＜100万）</span></label><el-input v-model.trim="form.step2.growthAnchorCount" placeholder="人" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="chips-block">
          <label>主播覆盖垂类 <span class="required">*</span></label>
          <el-checkbox-group v-model="form.step2.coverCategories" class="chips-group" :disabled="readonlyMode">
            <el-checkbox v-for="item in extendedCategoryOptions" :key="item.value" :label="item.value">{{ item.label }}</el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="sub-section">孵化投入成本</div>
        <div class="textarea-item"><label>孵化周期与过程描述</label><el-input v-model.trim="form.step2.incubationDescription" type="textarea" :rows="4" resize="none" placeholder="描述主播从签约到成熟的标准孵化流程、典型周期（月）、各阶段的投入资源（培训、流量投放、内容团队配置等）" class="control textarea" :disabled="readonlyMode" /></div>
        <div class="form-grid">
          <div class="field-item"><label>单主播平均孵化成本（万元）</label><el-input v-model.trim="form.step2.avgIncubationCost" placeholder="万元/人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>年度孵化总投入（万元）</label><el-input v-model.trim="form.step2.totalIncubationCost" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
        </div>
      </template>

      <template v-if="currentStep === 3">
        <div class="panel-title">三、主播内容与影响力评估</div>
        <div class="panel-sub">填写MCN机构旗下主播的内容能力数据及资产估值信息</div>

        <div class="sub-section">主播资产综合估值</div>
        <div class="field-item estimate-row"><label>主播资产综合估值（万元） <span class="required">*</span></label><el-input v-model.trim="form.step3.assetValuation" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
        <div class="textarea-item"><label>估值说明</label><el-input v-model.trim="form.step3.valuationDescription" type="textarea" :rows="3" resize="none" placeholder="基于主播粉丝规模、商业化效率、内容创作能力、平台生态适配度等多维度综合测算的估值说明" class="control textarea" :disabled="readonlyMode" /></div>

        <div class="sub-section">内容创作与粉丝质量数据</div>
        <div class="form-grid three-grid">
          <div class="field-item"><label>短视频月均更新量</label><el-input v-model.trim="form.step3.monthlyUpdateCount" placeholder="条" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>短视频爆款率 <span class="minor-tip">（播放≥100万）</span></label><el-input v-model.trim="form.step3.viralRate" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>直播间平均停留时长（秒）</label><el-input v-model.trim="form.step3.avgStaySeconds" placeholder="秒" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>粉丝互动率</label><el-input v-model.trim="form.step3.fanInteractionRate" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>直播间互动率</label><el-input v-model.trim="form.step3.liveInteractionRate" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>跨平台账号总数</label><el-input v-model.trim="form.step3.crossPlatformAccountCount" placeholder="个" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="sub-section">合规与风控体系说明</div>
        <div class="textarea-item"><label>选品审核流程说明</label><el-input v-model.trim="form.step3.selectionProcess" type="textarea" :rows="3" resize="none" placeholder="描述机构的选品审核流程、标准及执行情况..." class="control textarea" :disabled="readonlyMode" /></div>
        <div class="textarea-item"><label>内容合规机制</label><el-input v-model.trim="form.step3.contentCompliance" type="textarea" :rows="3" resize="none" placeholder="描述内容审核机制、合规团队配置及相关制度建设情况..." class="control textarea" :disabled="readonlyMode" /></div>
      </template>

      <template v-if="currentStep === 4">
        <div class="panel-title">四、主播账号资产与权益数据</div>
        <div class="panel-sub">上传主播账号权属证明、作品著作权及商业合约等文件</div>

        <div class="sub-section">主播账号权属信息</div>
        <div class="field-item upload-top-field"><label>账号归属主体 <span class="required">*</span> <span class="minor-tip">（可多选）</span></label><el-select v-model="form.step4.ownershipSubjects" multiple placeholder="请选择" class="control upload-select" :disabled="readonlyMode"><el-option v-for="item in ownershipSubjectOptions" :key="item.value" :label="item.label" :value="item.value" /></el-select></div>

        <div class="upload-block"><div class="upload-title">上传授权文件 <span class="required">*</span></div><button type="button" class="upload-area" :disabled="readonlyMode || isUploadingFile('authorizationFiles')" @click="triggerFile('authorizationFile')"><div class="upload-emoji folder">📁</div><div class="upload-text">点击上传授权文件</div><div class="upload-sub">最多上传 50 个文件，支持 PDF、JPG、PNG、DOC、DOCX，单个文件不超过 30MB</div></button><input ref="authorizationFile" type="file" multiple class="hidden-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="handleFileChange($event, 'authorizationFiles')" /><div v-if="form.step4.authorizationFiles.length" class="uploaded-file-list"><div v-for="(file, index) in form.step4.authorizationFiles" :key="`${file.url}-${index}`" class="uploaded-file-item"><a :href="file.url" target="_blank" rel="noopener noreferrer" class="uploaded-file-link">{{ file.name }}</a><button v-if="!readonlyMode" type="button" class="remove-file-btn" @click="removeUploadedFile('authorizationFiles', index)">删除</button></div></div></div>
        <div class="sub-section">使用权内主播账号</div>
        <div class="upload-block"><div class="upload-title">上传授权文件 <span class="required">*</span></div><button type="button" class="upload-area" :disabled="readonlyMode || isUploadingFile('usageFiles')" @click="triggerFile('usageFile')"><div class="upload-emoji folder">📁</div><div class="upload-text">点击上传主播账号使用权授权文件</div><div class="upload-sub">最多上传 50 个文件，支持 PDF、JPG、PNG、DOC、DOCX，单个文件不超过 30MB</div></button><input ref="usageFile" type="file" multiple class="hidden-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="handleFileChange($event, 'usageFiles')" /><div v-if="form.step4.usageFiles.length" class="uploaded-file-list"><div v-for="(file, index) in form.step4.usageFiles" :key="`${file.url}-${index}`" class="uploaded-file-item"><a :href="file.url" target="_blank" rel="noopener noreferrer" class="uploaded-file-link">{{ file.name }}</a><button v-if="!readonlyMode" type="button" class="remove-file-btn" @click="removeUploadedFile('usageFiles', index)">删除</button></div></div></div>
        <div class="sub-section">收益权内主播账号</div>
        <div class="upload-block"><div class="upload-title">上传授权文件 <span class="required">*</span></div><button type="button" class="upload-area" :disabled="readonlyMode || isUploadingFile('incomeFiles')" @click="triggerFile('incomeFile')"><div class="upload-emoji folder">📁</div><div class="upload-text">点击上传主播账号收益权授权文件</div><div class="upload-sub">最多上传 50 个文件，支持 PDF、JPG、PNG、DOC、DOCX，单个文件不超过 30MB</div></button><input ref="incomeFile" type="file" multiple class="hidden-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="handleFileChange($event, 'incomeFiles')" /><div v-if="form.step4.incomeFiles.length" class="uploaded-file-list"><div v-for="(file, index) in form.step4.incomeFiles" :key="`${file.url}-${index}`" class="uploaded-file-item"><a :href="file.url" target="_blank" rel="noopener noreferrer" class="uploaded-file-link">{{ file.name }}</a><button v-if="!readonlyMode" type="button" class="remove-file-btn" @click="removeUploadedFile('incomeFiles', index)">删除</button></div></div></div>
        <div class="sub-section">作品著作权与内容资产处置权</div>
        <div class="upload-block"><div class="upload-title">上传证明文件 <span class="required">*</span></div><button type="button" class="upload-area" :disabled="readonlyMode || isUploadingFile('copyrightFiles')" @click="triggerFile('copyrightFile')"><div class="upload-emoji doc">📄</div><div class="upload-text">点击上传著作权登记证书、内容版权证明等</div><div class="upload-sub">最多上传 50 个文件，支持 PDF、JPG、PNG、DOC、DOCX，单个文件不超过 30MB</div></button><input ref="copyrightFile" type="file" multiple class="hidden-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="handleFileChange($event, 'copyrightFiles')" /><div v-if="form.step4.copyrightFiles.length" class="uploaded-file-list"><div v-for="(file, index) in form.step4.copyrightFiles" :key="`${file.url}-${index}`" class="uploaded-file-item"><a :href="file.url" target="_blank" rel="noopener noreferrer" class="uploaded-file-link">{{ file.name }}</a><button v-if="!readonlyMode" type="button" class="remove-file-btn" @click="removeUploadedFile('copyrightFiles', index)">删除</button></div></div></div>
        <div class="sub-section">商业合约与品牌合作</div>
        <div class="upload-block"><div class="upload-title">上传证明文件 <span class="required">*</span></div><button type="button" class="upload-area" :disabled="readonlyMode || isUploadingFile('commercialFiles')" @click="triggerFile('commercialFile')"><div class="upload-emoji doc">📋</div><div class="upload-text">点击上传品牌合作协议、商业合同摘要等</div><div class="upload-sub">最多上传 50 个文件，支持 PDF、JPG、PNG、DOC、DOCX，单个文件不超过 30MB，敏感信息可脱敏处理</div></button><input ref="commercialFile" type="file" multiple class="hidden-input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="handleFileChange($event, 'commercialFiles')" /><div v-if="form.step4.commercialFiles.length" class="uploaded-file-list"><div v-for="(file, index) in form.step4.commercialFiles" :key="`${file.url}-${index}`" class="uploaded-file-item"><a :href="file.url" target="_blank" rel="noopener noreferrer" class="uploaded-file-link">{{ file.name }}</a><button v-if="!readonlyMode" type="button" class="remove-file-btn" @click="removeUploadedFile('commercialFiles', index)">删除</button></div></div></div>
      </template>

      <template v-if="currentStep === 5">
        <div class="panel-title">五、主播运营数据情况</div>
        <div class="panel-sub">录入旗下核心主播的运营数据，至少添加一位主播信息</div>

        <div v-for="(item, index) in form.step5.anchorMetrics" :key="item.id" class="anchor-card">
          <div class="anchor-card-head">
            <strong>主播 #{{ index + 1 }}</strong>
            <button v-if="!readonlyMode && form.step5.anchorMetrics.length > 1" type="button" class="remove-anchor-btn" @click="removeAnchorMetric(index)">×</button>
          </div>
          <div class="form-grid three-grid anchor-grid">
            <div class="field-item wide-two"><label>全网各平台ID账号</label><el-input v-model.trim="item.platformAccounts" placeholder="请填写，例：抖音：dy123454552，小红书：34455666767" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>主播名称 <span class="required">*</span></label><el-select v-model="item.streamerProfileId" filterable placeholder="请选择主播" class="control" :disabled="readonlyMode || isLoadingStreamerProfile(item.id)" @change="fillAnchorMetric(item)"><el-option v-for="option in streamerOptions" :key="option.id" :label="option.name" :value="option.id" /></el-select></div>
            <div class="field-item"><label>全网粉丝总数（万）</label><el-input v-model.trim="item.totalFans" placeholder="万" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>全网作品点击量（万）</label><el-input v-model.trim="item.totalViews" placeholder="万" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>近一年直播场次</label><el-input v-model.trim="item.liveSessions" placeholder="场" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>近一年利润（万元）</label><el-input v-model.trim="item.annualProfit" placeholder="万" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>场均观看（万）</label><el-input v-model.trim="item.avgViewers" placeholder="万" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>粉丝增长率（月均%）</label><el-input v-model.trim="item.fanGrowthRate" placeholder="%" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>直播转化率（%）</label><el-input v-model.trim="item.conversionRate" placeholder="%" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>场均GMV（万元）</label><el-input v-model.trim="item.avgGmv" placeholder="万" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>客单价（元）</label><el-input v-model.trim="item.unitPrice" placeholder="元" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item wide-two"><label>近12个月GMV（万元）</label><el-input v-model.trim="item.last12MonthGmv" placeholder="万" class="control" :disabled="readonlyMode" /></div>
            <div class="field-item"><label>垂类赛道</label><el-select v-model="item.category" placeholder="请选择垂类赛道" class="control" :disabled="readonlyMode"><el-option v-for="option in extendedCategoryOptions" :key="option.value" :label="option.label" :value="option.value" /></el-select></div>
          </div>
        </div>

        <button v-if="!readonlyMode" type="button" class="add-anchor-btn" @click="addAnchorMetric">+ 添加主播</button>

        <div class="sub-section">总体运营指标汇总</div>
        <div class="form-grid three-grid">
          <div class="field-item"><label>机构年度总GMV（万元） <span class="required">*</span></label><el-input v-model.trim="form.step5.summary.annualGMV" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>机构年度总营收（万元） <span class="required">*</span></label><el-input v-model.trim="form.step5.summary.annualRevenue" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>机构年度净利润（万元） <span class="required">*</span></label><el-input v-model.trim="form.step5.summary.annualNetProfit" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>近12个月直播场次</label><el-input v-model.trim="form.step5.summary.last12MonthsSessions" placeholder="场" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>场均观看人数</label><el-input v-model.trim="form.step5.summary.avgViewersCount" placeholder="万人" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>总粉丝覆盖量</label><el-input v-model.trim="form.step5.summary.totalFanCoverage" placeholder="万人" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="sub-section">收入结构与成本分析</div>
        <div class="form-grid two-grid">
          <div class="field-item"><label>佣金收入占比（%）</label><el-input v-model.trim="form.step5.incomeStructure.commissionRatio" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>坑位费收入占比（%）</label><el-input v-model.trim="form.step5.incomeStructure.slotFeeRatio" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>品牌营销收入占比（%）</label><el-input v-model.trim="form.step5.incomeStructure.brandIncomeRatio" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>其他收入占比（%）</label><el-input v-model.trim="form.step5.incomeStructure.otherIncomeRatio" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>运营成本率（%）</label><el-input v-model.trim="form.step5.incomeStructure.costRatio" placeholder="%" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>净利润率（%）</label><el-input v-model.trim="form.step5.incomeStructure.netProfitRatio" placeholder="%" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="sub-section">收入预测 <span class="minor-tip">（综合多平台数据测算）</span></div>
        <div class="form-grid two-grid">
          <div class="field-item"><label>短期收益-未来6个月（万元） <span class="required">*</span></label><el-input v-model.trim="form.step5.forecast.shortTermIncome6" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>全生命周期收益-3年预估（万元） <span class="required">*</span></label><el-input v-model.trim="form.step5.forecast.lifecycleIncome3" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
        </div>
        <div class="textarea-item"><label>收益测算依据 <span class="required">*</span></label><el-input v-model.trim="form.step5.forecast.forecastBasis" type="textarea" :rows="3" resize="none" placeholder="说明上述收益预测的测算方法和依据..." class="control textarea" :disabled="readonlyMode" /></div>
        <div class="textarea-item"><label>第三方支撑与生态协作 <span class="required">*</span> <span class="minor-tip">（如带货数据平台、第三方监测报告等）</span></label><el-input v-model.trim="form.step5.forecast.thirdPartySupport" type="textarea" :rows="3" resize="none" placeholder="列出提供数据支撑的第三方平台或机构..." class="control textarea" :disabled="readonlyMode" /></div>
      </template>

      <template v-if="currentStep === 6">
        <div class="panel-title">六、商业化情况评估</div>
        <div class="panel-sub">汇总MCN机构的商业化能力及未来收益预测</div>

        <div class="sub-section">收入预测</div>
        <div class="form-grid two-grid">
          <div class="field-item"><label>短期收益-未来12个月（万元） <span class="required">*</span></label><el-input v-model.trim="form.step6.shortTermIncome12" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
          <div class="field-item"><label>全生命周期收益-5年（万元） <span class="required">*</span></label><el-input v-model.trim="form.step6.lifecycleIncome5" placeholder="万元" class="control" :disabled="readonlyMode" /></div>
        </div>

        <div class="sub-section">评估申请确认与提交</div>
        <div class="confirm-box">
          <div class="confirm-title">提交前确认</div>
          <ul>
            <li>以上所填信息真实、准确、完整，如有虚假，愿承担相应法律责任</li>
            <li>授权四川数字世界文化科技有限公司对提交数据进行核验</li>
            <li>评估报告仅供银行信贷、投融资等金融服务参考使用</li>
            <li>上传的权属文件及商业合约将严格保密，仅用于本次资产评估</li>
          </ul>
        </div>

      </template>
    </section>

    <div v-if="!readonlyMode" class="footer-actions" :class="{ split: currentStep > 1 }">
      <button v-if="currentStep > 1" type="button" class="footer-btn back" @click="goPrev">← 上一步</button>
      <div class="right-actions">
        <button type="button" class="footer-btn ghost" :disabled="isSaving || isSubmitting" @click="saveDraft">{{ isSaving ? '保存中...' : '保存草稿' }}</button>
        <button v-if="currentStep < 6" type="button" class="footer-btn primary" :disabled="isSaving || isSubmitting" @click="goNext">下一步 →</button>
        <button v-else type="button" class="footer-btn submit" :disabled="isSaving || isSubmitting" @click="submitApplication">{{ isSubmitting ? '提交中...' : '提交评估申请' }}</button>
      </div>
    </div>
  </UserShell>
</template>

<script>
import UserShell from './components/UserShell.vue';
import sidebarBg from '@/assets/img/user/背景卡图.png';
import userAvatar from '@/assets/img/user/avatar.png';
import overviewActiveIcon from '@/assets/img/user/griconxz.png';
import overviewActiveIconIn from '@/assets/img/user/gricon.png';
import talentActiveIcon from '@/assets/img/user/zbglxz.png';
import talentInactiveIcon from '@/assets/img/user/zbgl.png';
import assetActiveIcon from '@/assets/img/user/申请icon.png';
import assetInactiveIcon from '@/assets/img/user/申请未选中.png';
import tipIcon from '@/assets/img/user/提示.png';
import { getMemberProfile, getStreamerProfileDetail, getStreamerProfileList, uploadMemberFile } from '@/api/user';
import {
  createEmptyOrganizationAssetAnchorMetric,
  createEmptyOrganizationAssetForm,
  getDefaultOrganizationAssetOptions,
  getOrganizationAssetApplicationById,
  getOrganizationAssetApplicationDraft,
  getOrganizationAssetOptions,
  saveOrganizationAssetApplicationDraft,
  submitOrganizationAssetApplication
} from './organizationAssetStore';

function createMenu() {
  return [
    { key: 'overview', label: '首页概览', path: '/user/org', activeIcon: overviewActiveIcon, inactiveIcon: overviewActiveIconIn },
    { key: 'talent', label: '主播管理', path: '/user/org/talents', activeIcon: talentActiveIcon, inactiveIcon: talentInactiveIcon },
    { key: 'asset', label: '资产评估申请', path: '/user/org/assets', activeIcon: assetActiveIcon, inactiveIcon: assetInactiveIcon }
  ];
}

function extractResponseData(response) {
  return response && response.data ? response.data : {};
}

function extractUploadUrl(response) {
  const data = response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;

  if (typeof data === 'string') {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return '';
  }

  return data.url || data.full_url || data.path || data.location || '';
}

function createRegionOptions() {
  return [
    { value: '北京市', label: '北京市', children: [{ value: '北京市', label: '北京市' }] },
    { value: '天津市', label: '天津市', children: [{ value: '天津市', label: '天津市' }] },
    { value: '上海市', label: '上海市', children: [{ value: '上海市', label: '上海市' }] },
    { value: '重庆市', label: '重庆市', children: [{ value: '重庆市', label: '重庆市' }] },
    { value: '四川省', label: '四川省', children: [{ value: '成都市', label: '成都市' }, { value: '绵阳市', label: '绵阳市' }, { value: '德阳市', label: '德阳市' }, { value: '乐山市', label: '乐山市' }, { value: '宜宾市', label: '宜宾市' }] },
    { value: '广东省', label: '广东省', children: [{ value: '广州市', label: '广州市' }, { value: '深圳市', label: '深圳市' }, { value: '佛山市', label: '佛山市' }, { value: '东莞市', label: '东莞市' }, { value: '珠海市', label: '珠海市' }] },
    { value: '浙江省', label: '浙江省', children: [{ value: '杭州市', label: '杭州市' }, { value: '宁波市', label: '宁波市' }, { value: '温州市', label: '温州市' }, { value: '嘉兴市', label: '嘉兴市' }, { value: '金华市', label: '金华市' }] },
    { value: '江苏省', label: '江苏省', children: [{ value: '南京市', label: '南京市' }, { value: '苏州市', label: '苏州市' }, { value: '无锡市', label: '无锡市' }, { value: '常州市', label: '常州市' }, { value: '南通市', label: '南通市' }] },
    { value: '山东省', label: '山东省', children: [{ value: '济南市', label: '济南市' }, { value: '青岛市', label: '青岛市' }, { value: '烟台市', label: '烟台市' }, { value: '潍坊市', label: '潍坊市' }, { value: '临沂市', label: '临沂市' }] },
    { value: '福建省', label: '福建省', children: [{ value: '福州市', label: '福州市' }, { value: '厦门市', label: '厦门市' }, { value: '泉州市', label: '泉州市' }, { value: '漳州市', label: '漳州市' }] },
    { value: '湖北省', label: '湖北省', children: [{ value: '武汉市', label: '武汉市' }, { value: '襄阳市', label: '襄阳市' }, { value: '宜昌市', label: '宜昌市' }] },
    { value: '湖南省', label: '湖南省', children: [{ value: '长沙市', label: '长沙市' }, { value: '株洲市', label: '株洲市' }, { value: '岳阳市', label: '岳阳市' }] },
    { value: '河南省', label: '河南省', children: [{ value: '郑州市', label: '郑州市' }, { value: '洛阳市', label: '洛阳市' }, { value: '南阳市', label: '南阳市' }] },
    { value: '河北省', label: '河北省', children: [{ value: '石家庄市', label: '石家庄市' }, { value: '唐山市', label: '唐山市' }, { value: '保定市', label: '保定市' }] },
    { value: '陕西省', label: '陕西省', children: [{ value: '西安市', label: '西安市' }, { value: '咸阳市', label: '咸阳市' }, { value: '榆林市', label: '榆林市' }] },
    { value: '辽宁省', label: '辽宁省', children: [{ value: '沈阳市', label: '沈阳市' }, { value: '大连市', label: '大连市' }, { value: '鞍山市', label: '鞍山市' }] },
    { value: '江西省', label: '江西省', children: [{ value: '南昌市', label: '南昌市' }, { value: '赣州市', label: '赣州市' }, { value: '九江市', label: '九江市' }] },
    { value: '安徽省', label: '安徽省', children: [{ value: '合肥市', label: '合肥市' }, { value: '芜湖市', label: '芜湖市' }, { value: '阜阳市', label: '阜阳市' }] }
  ];
}

function parseOfficeRegion(value) {
  const parts = String(value || '').split('/').map(item => item.trim()).filter(Boolean);
  return parts.length >= 2 ? parts.slice(0, 2) : [];
}

export default {
  name: 'OrganizationAssetApplicationCreate',
  components: { UserShell },
  data() {
    const defaultOptions = getDefaultOrganizationAssetOptions();
    return {
      tipIcon,
      profile: {
        name: '星辰传媒有限公司',
        role: 'MCN机构',
        avatar: userAvatar,
        background: sidebarBg
      },
      memberProfile: {},
      rejectReason: '',
      officeRegion: [],
      regionOptions: createRegionOptions(),
      menuItems: createMenu(),
      readonlyMode: false,
      currentId: '',
      sourceEvaluationId: '',
      currentStatus: '',
      currentStep: 1,
      form: createEmptyOrganizationAssetForm(),
      isSaving: false,
      isSubmitting: false,
      uploadingFiles: {},
      streamerOptions: [],
      loadingStreamerProfiles: {},
      steps: [
        { no: 1, label: '基本信息' },
        { no: 2, label: '孵化数据' },
        { no: 3, label: '内容影响力' },
        { no: 4, label: '账号资产' },
        { no: 5, label: '运营数据' },
        { no: 6, label: '商业化评估' }
      ],
      businessScopeOptions: defaultOptions.business_scope,
      corePlatformOptions: defaultOptions.mcn_platform,
      certificateOptions: defaultOptions.qualification,
      extendedCategoryOptions: defaultOptions.vertical_track,
      ownershipSubjectOptions: defaultOptions.account_ownership_type
    };
  },
  created() {
    this.initPage();
  },
  methods: {
    normalizeStep(value) {
      const step = Number(value);
      if (!Number.isFinite(step) || step < 1) {
        return 1;
      }
      if (step > this.steps.length) {
        return this.steps.length;
      }
      return step;
    },
    getProfileDefaults() {
      return {
        companyName: this.memberProfile.name || '',
        creditCode: this.memberProfile.company_code || ''
      };
    },
    async loadProfile() {
      try {
        const profileResponse = await getMemberProfile();
        const profileData = extractResponseData(profileResponse);
        this.memberProfile = profileData;
        this.profile = {
          ...this.profile,
          name: profileData.name || this.profile.name,
          role: profileData.type_text || this.profile.role,
          avatar: profileData.avatar || this.profile.avatar
        };
      } catch (error) {
        void error;
      }
    },
    async loadOptions() {
      try {
        const options = await getOrganizationAssetOptions();
        this.businessScopeOptions = options.business_scope;
        this.corePlatformOptions = options.mcn_platform;
        this.certificateOptions = options.qualification;
        this.extendedCategoryOptions = options.vertical_track;
        this.ownershipSubjectOptions = options.account_ownership_type;
      } catch (error) {
        void error;
      }
    },
    async loadStreamerProfiles() {
      const perPage = 100;
      let page = 1;
      const streamers = [];

      try {
        while (true) {
          const response = await getStreamerProfileList({ page, per_page: perPage });
          const payload = response && Array.isArray(response.data) ? response : extractResponseData(response);
          const rows = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
          const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {};
          streamers.push(...rows);

          const total = Number(meta.total);
          if (!rows.length || (total && streamers.length >= total) || (!total && rows.length < perPage)) {
            break;
          }
          page += 1;
        }

        this.streamerOptions = streamers
          .filter(item => item && item.id != null && item.name)
          .map(item => ({ id: item.id, name: item.name }));
        this.matchExistingAnchorMetrics();
      } catch (error) {
        this.streamerOptions = [];
        this.$message.error(error && error.message ? error.message : '获取主播列表失败');
      }
    },
    matchExistingAnchorMetrics() {
      const anchorMetrics = this.form && this.form.step5 && Array.isArray(this.form.step5.anchorMetrics)
        ? this.form.step5.anchorMetrics
        : [];
      anchorMetrics.forEach(item => {
        if (item.streamerProfileId || !item.anchorName) {
          return;
        }
        const matches = this.streamerOptions.filter(option => option.name === item.anchorName);
        if (matches.length === 1) {
          this.$set(item, 'streamerProfileId', matches[0].id);
        }
      });
    },
    async initPage() {
      const { mode, id, source_evaluation_id: sourceEvaluationId, step } = this.$route.query;
      this.readonlyMode = mode === 'view';
      this.currentId = id || '';
      this.sourceEvaluationId = sourceEvaluationId || '';
      this.currentStatus = '';
      this.currentStep = this.normalizeStep(step || 1);
      this.rejectReason = '';

      await Promise.all([this.loadProfile(), this.loadOptions(), this.loadStreamerProfiles()]);
      const preset = this.getProfileDefaults();

      try {
        if (this.readonlyMode && this.currentId) {
          const record = await getOrganizationAssetApplicationById(this.currentId, preset);
          if (!record) {
            this.$message.warning('未找到对应评估记录');
            this.$router.replace('/user/org/assets');
            return;
          }
          const recordStatus = record && record.raw ? (record.raw.status || '') : '';
          this.currentStatus = recordStatus;
          this.readonlyMode = mode === 'view' && recordStatus !== 'approved';
          this.form = record.form;
          this.rejectReason = record && record.raw ? (record.raw.reject_reason || '') : '';
          this.officeRegion = parseOfficeRegion(this.form.officeAddress);
          this.matchExistingAnchorMetrics();
          return;
        }

        const draft = await getOrganizationAssetApplicationDraft(preset);
        if (draft) {
          this.form = draft.form;
          this.sourceEvaluationId = this.sourceEvaluationId || draft.sourceEvaluationId;
          this.currentStatus = draft.raw && draft.raw.source_evaluation_status ? draft.raw.source_evaluation_status : '';
          this.rejectReason = draft.raw && draft.raw.reject_reason ? draft.raw.reject_reason : '';
          this.officeRegion = parseOfficeRegion(this.form.officeAddress);
          this.matchExistingAnchorMetrics();
          return;
        }

        if (this.sourceEvaluationId) {
          const record = await getOrganizationAssetApplicationById(this.sourceEvaluationId, preset);
          if (!record) {
            this.$message.warning('未找到对应评估记录');
            this.$router.replace('/user/org/assets');
            return;
          }
          this.currentStatus = record.raw ? (record.raw.status || '') : '';
          this.form = record.form;
          this.rejectReason = record.raw ? (record.raw.reject_reason || '') : '';
          this.officeRegion = parseOfficeRegion(this.form.officeAddress);
          this.matchExistingAnchorMetrics();
          return;
        }

        this.form = createEmptyOrganizationAssetForm(preset);
        this.officeRegion = parseOfficeRegion(this.form.officeAddress);
        this.matchExistingAnchorMetrics();
      } catch (error) {
        this.form = createEmptyOrganizationAssetForm(preset);
        this.officeRegion = parseOfficeRegion(this.form.officeAddress);
        this.$message.error(error && error.message ? error.message : '获取评估信息失败');
      }
    },
    stepState(no) {
      if (no < this.currentStep) {
        return 'done';
      }
      if (no === this.currentStep) {
        return 'active';
      }
      return 'pending';
    },
    jumpToStep(no) {
      this.currentStep = no;
      this.syncStepQuery();
    },
    syncStepQuery() {
      this.$router.replace({ query: { ...this.$route.query, step: String(this.currentStep) } });
    },
    syncOfficeRegion(value) {
      const region = Array.isArray(value) ? value : this.officeRegion;
      this.form.officeAddress = region.length >= 2 ? `${region[0]}/${region[1]}` : '';
    },
    removeUploadedFile(field, index) {
      const files = this.form && this.form.step4 && this.form.step4[field];
      if (this.readonlyMode || !Array.isArray(files)) {
        return;
      }
      files.splice(index, 1);
    },
    triggerFile(refName) {
      if (this.readonlyMode) {
        return;
      }
      const ref = this.$refs[refName];
      const target = Array.isArray(ref) ? ref[0] : ref;
      if (target) {
        target.click();
      }
    },
    isUploadingFile(field) {
      return Boolean(this.uploadingFiles[field]);
    },
    isLoadingStreamerProfile(metricId) {
      return Boolean(this.loadingStreamerProfiles[metricId]);
    },
    async fillAnchorMetric(metric) {
      const streamerProfileId = metric.streamerProfileId;
      if (!streamerProfileId) {
        metric.anchorName = '';
        return;
      }

      this.$set(this.loadingStreamerProfiles, metric.id, true);
      try {
        const response = await getStreamerProfileDetail(streamerProfileId);
        const streamer = extractResponseData(response);
        if (String(metric.streamerProfileId) !== String(streamerProfileId)) {
          return;
        }

        const platformAccounts = Array.isArray(streamer.platform_accounts)
          ? streamer.platform_accounts
          : (Array.isArray(streamer.platforms) ? streamer.platforms : []);
        const primaryAccount = platformAccounts[0] || {};
        metric.platformAccounts = platformAccounts
          .map(account => account && (account.platform_id || account.account_no))
          .filter(Boolean)
          .join('、');
        metric.anchorName = streamer.name || '';
        metric.totalFans = streamer.total_fans || '';
        metric.totalViews = streamer.total_clicks || '';
        metric.liveSessions = streamer.last_year_live_sessions || '';
        metric.annualProfit = streamer.last_year_profit || '';
        metric.category = primaryAccount.vertical_track || primaryAccount.category || '';
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '获取主播信息失败');
      } finally {
        this.$set(this.loadingStreamerProfiles, metric.id, false);
      }
    },
    async handleFileChange(event, field) {
      const files = Array.from(event.target.files || []);
      if (!files.length || this.isUploadingFile(field)) {
        return;
      }

      const allowExt = ['png', 'jpg', 'jpeg', 'pdf', 'doc', 'docx'];
      const currentFiles = Array.isArray(this.form.step4[field]) ? this.form.step4[field] : [];
      if (currentFiles.length + files.length > 50) {
        this.$message.warning('每项最多上传50个文件');
        event.target.value = '';
        return;
      }

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const ext = String(file.name).split('.').pop().toLowerCase();
        if (!allowExt.includes(ext)) {
          this.$message.warning('请上传 png、jpg、jpeg、pdf、doc、docx 格式文件');
          event.target.value = '';
          return;
        }
        if (file.size > 30 * 1024 * 1024) {
          this.$message.warning('文件大小不能超过30M');
          event.target.value = '';
          return;
        }
      }

      this.$set(this.uploadingFiles, field, true);
      try {
        const results = await Promise.all(files.map(async file => {
          try {
            const response = await uploadMemberFile(file, 'evidence');
            const fileUrl = extractUploadUrl(response);
            if (!fileUrl) {
              throw new Error('上传成功，但未返回文件地址');
            }
            return { name: file.name, url: fileUrl };
          } catch (error) {
            return null;
          }
        }));
        const uploadedFiles = results.filter(Boolean);
        this.form.step4[field] = [...currentFiles, ...uploadedFiles];
        if (uploadedFiles.length) {
          this.$message.success(uploadedFiles.length === files.length ? '文件上传成功' : '部分文件上传失败');
        } else {
          this.$message.error('文件上传失败');
        }
      } finally {
        this.$set(this.uploadingFiles, field, false);
        event.target.value = '';
      }
    },
    addAnchorMetric() {
      this.form.step5.anchorMetrics.push(createEmptyOrganizationAssetAnchorMetric());
    },
    removeAnchorMetric(index) {
      if (this.form.step5.anchorMetrics.length === 1) {
        return;
      }
      this.form.step5.anchorMetrics.splice(index, 1);
    },
    validateStep(stepNo, switchStepOnError = false) {
      const fail = (message) => {
        if (switchStepOnError) {
          this.currentStep = stepNo;
          this.syncStepQuery();
        }
        this.$message.warning(message);
        return false;
      };

      if (stepNo === 1) {
        const checks = [
          ['companyName', '请输入MCN机构全称'],
          ['creditCode', '请输入统一社会信用代码'],
          ['registerCapital', '请输入注册资本'],
          ['paidCapital', '请输入实缴资本'],
          ['shareholders', '请输入股权结构'],
          ['foundedAt', '请选择成立时间'],
          ['staffCount', '请输入全职员工人数'],
          ['anchorCount', '请输入签约主播人数'],
          ['officeAddress', '请输入机构办公地址']
        ];
        for (let index = 0; index < checks.length; index += 1) {
          const [field, message] = checks[index];
          if (!this.form[field]) {
            return fail(message);
          }
        }
        if (!this.form.businessScopes.length) {
          return fail('请选择主营业务范围');
        }
        if (!this.form.corePlatforms.length) {
          return fail('请选择核心合作平台');
        }
      }

      if (stepNo === 2) {
        if (!this.form.step2.signedAnchorCount || !this.form.step2.exclusiveAnchorCount) {
          return fail('请完整填写主播签约结构');
        }
        if (!this.form.step2.coverCategories.length) {
          return fail('请选择主播覆盖垂类');
        }
      }

      if (stepNo === 3 && !this.form.step3.assetValuation) {
        return fail('请输入主播资产综合估值');
      }

      if (stepNo === 4) {
        if (!this.form.step4.ownershipSubjects.length) {
          return fail('请选择账号归属主体');
        }
        const requiredFiles = [
          ['authorizationFiles', '请上传账号归属授权文件'],
          ['usageFiles', '请上传主播账号使用权授权文件'],
          ['incomeFiles', '请上传主播账号收益权授权文件'],
          ['copyrightFiles', '请上传作品著作权与内容资产处置权证明文件'],
          ['commercialFiles', '请上传商业合约与品牌合作证明文件']
        ];
        const missingFiles = requiredFiles.find(([field]) => !Array.isArray(this.form.step4[field]) || !this.form.step4[field].length);
        if (missingFiles) {
          return fail(missingFiles[1]);
        }
      }

      if (stepNo === 5) {
        if (!this.form.step5.anchorMetrics.length) {
          return fail('请至少添加一位主播');
        }
        const invalidAnchor = this.form.step5.anchorMetrics.some(item => !item.anchorName);
        if (invalidAnchor) {
          return fail('请完整填写主播名称');
        }
        if (!this.form.step5.summary.annualGMV || !this.form.step5.summary.annualRevenue || !this.form.step5.summary.annualNetProfit) {
          return fail('请填写总体运营指标汇总');
        }
        const forecast = this.form.step5.forecast;
        if (!forecast.shortTermIncome6 || !forecast.lifecycleIncome3 || !forecast.forecastBasis || !forecast.thirdPartySupport) {
          return fail('请完整填写收入预测');
        }
      }

      if (stepNo === 6) {
        if (!this.form.step6.shortTermIncome12 || !this.form.step6.lifecycleIncome5) {
          return fail('请填写商业化收益预测');
        }
      }

      return true;
    },
    validateCurrentStep() {
      return this.validateStep(this.currentStep, false);
    },
    validateAllSteps() {
      for (let step = 1; step <= this.steps.length; step += 1) {
        if (!this.validateStep(step, true)) {
          return false;
        }
      }
      return true;
    },
    getSourceEvaluationId() {
      return this.sourceEvaluationId || undefined;
    },
    async persistDraft(successMessage = '草稿已保存') {
      const draft = await saveOrganizationAssetApplicationDraft(this.form, this.getSourceEvaluationId());
      if (draft) {
        this.form = draft;
      }
      this.$message.success(successMessage);
    },
    async saveDraft() {
      if (this.readonlyMode || this.isSaving || this.isSubmitting) {
        return;
      }
      this.isSaving = true;
      try {
        await this.persistDraft('草稿已保存');
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '保存草稿失败');
      } finally {
        this.isSaving = false;
      }
    },
    goPrev() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
        this.syncStepQuery();
      }
    },
    goNext() {
      if (this.readonlyMode || this.isSaving || this.isSubmitting || !this.validateCurrentStep()) {
        return;
      }
      if (this.currentStep < this.steps.length) {
        this.currentStep += 1;
        this.syncStepQuery();
      }
    },
    async submitApplication() {
      if (this.readonlyMode || this.isSubmitting || !this.validateAllSteps()) {
        return;
      }
      this.isSubmitting = true;
      try {
        await submitOrganizationAssetApplication(this.form, this.getSourceEvaluationId());
        this.$message.success('评估申请已提交');
        this.$router.push('/user/org/assets');
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '评估申请提交失败');
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  watch: {
    '$route.query'(nextQuery, prevQuery) {
      const nextMode = nextQuery && nextQuery.mode ? nextQuery.mode : '';
      const prevMode = prevQuery && prevQuery.mode ? prevQuery.mode : '';
      const nextId = nextQuery && nextQuery.id ? String(nextQuery.id) : '';
      const prevId = prevQuery && prevQuery.id ? String(prevQuery.id) : '';
      const nextSourceEvaluationId = nextQuery && nextQuery.source_evaluation_id ? String(nextQuery.source_evaluation_id) : '';
      const prevSourceEvaluationId = prevQuery && prevQuery.source_evaluation_id ? String(prevQuery.source_evaluation_id) : '';

      if (nextMode !== prevMode || nextId !== prevId || nextSourceEvaluationId !== prevSourceEvaluationId) {
        this.initPage();
        return;
      }

      const nextStep = this.normalizeStep(nextQuery && nextQuery.step ? nextQuery.step : 1);
      if (nextStep !== this.currentStep) {
        this.currentStep = nextStep;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.notice-box {
  display: flex;
  gap: 12px;
  padding: 16px 18px;
  border: 1px solid #a89eff;
  border-radius: 12px;
  background: rgba(111, 104, 243, 0.04);
  margin-top: 34px;
  color: #6f68f3;
  font-size: 15px;
}

.notice-icon {
  width: 20px;
  height: 20px;
  margin-top: 2px;
}

.notice-box strong {
  display: block;
  color: #6f68f3;
  font-size: 15px;
}

.notice-box p {
  margin-top: 8px;
  color: #7b87a4;
  font-size: 14px;
  line-height: 1.8;
}

.reject-box {
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fff1f2;
  color: #dc2626;
  font-size: 14px;
  line-height: 1.7;
}

.reject-label {
  font-weight: 600;
}

.reject-text {
  word-break: break-word;
}

.stepper {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 28px;
  flex-wrap: wrap;
}

.step-item {
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #c1c7d7;
  font-size: 14px;
  cursor: pointer;
}

.step-no {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #dde3ef;
  line-height: 32px;
  text-align: center;
}

.step-item.done {
  color: #18b79b;
}

.step-item.done .step-no {
  border: 0;
  background: #18b79b;
  color: #fff;
}

.step-item.active {
  color: #3a425a;
  font-weight: 600;
}

.step-item.active .step-no {
  border: 0;
  background: #6f68f3;
  color: #fff;
}

.panel {
  margin-top: 34px;
  padding: 24px 24px 18px;
  border-radius: 14px;
  background: #fff;
  box-shadow: inset 0 0 0 1px #eef1f7;
}

.panel-title {
  color: #3D3D3D;
  font-size: 20px;
  font-weight: 700;
}

.panel-sub {
  margin-top: 8px;
  color: #98a2b3;
  font-size: 14px;
}

.sub-section {
  margin-top: 24px;
  padding-left: 10px;
  border-left: 2px solid #4F46E5;
  color: #4F46E5;
  font-size: 18px;
  font-weight: 600;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  margin-top: 18px;
}

.three-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.two-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.top-grid .wide {
  grid-column: span 2;
}

.anchor-grid .wide-two {
  grid-column: span 2;
}

.field-item label,
.textarea-item label,
.chips-block label,
.upload-title {
  display: block;
  margin-bottom: 10px;
  color: #3D3D3D;
  font-size: 16px;
  font-weight: 600;
}

.minor-tip {
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
}

.required {
  color: #ff6a6a;
}

.control {
  width: 100%;
}

.control ::v-deep .el-input__inner,
.control ::v-deep .el-textarea__inner,
.control ::v-deep .el-select .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.control ::v-deep .el-input__inner {
  height: 42px;
}

.textarea-item {
  margin-top: 18px;
}

.textarea ::v-deep .el-textarea__inner {
  min-height: 72px !important;
}

.chips-block {
  margin-top: 22px;
}

.chips-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 10px;
}

.chips-group ::v-deep .el-checkbox {
  margin-right: 0;
}

.chips-group ::v-deep .el-checkbox__label {
  padding: 0 14px;
  height: 32px;
  border: 1px solid #dce2ef;
  border-radius: 999px;
  line-height: 32px;
  color: #667085;
}

.chips-group ::v-deep .el-checkbox__input {
  display: none;
}

.chips-group ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
  border-color: #6f68f3;
  background: #f0edff;
  color: #5f66ec;
}
.push-t-10{
	margin-top: 10px;
}
.dual-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.estimate-row {
  margin-top: 18px;
  max-width: 50%;
}

.upload-top-field {
  margin-top: 18px;
  max-width: 520px;
}

.upload-select {
  max-width: 420px;
}

.upload-block {
  margin-top: 20px;
}

.upload-area {
  width: 100%;
  min-height: 144px;
  border: 1px dashed #d9dfeb;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}

.upload-area:disabled {
  cursor: default;
}

.upload-emoji {
  font-size: 34px;
  line-height: 1;
}

.upload-text {
  color: #68758e;
  font-size: 16px;
}

.upload-sub {
  color: #a1a9c0;
  font-size: 13px;
}

.uploaded-file-list {
  margin-top: 10px;
  border: 1px solid #e1e6f0;
  border-radius: 8px;
  overflow: hidden;
}

.uploaded-file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 38px;
  padding: 0 12px;
  border-bottom: 1px solid #edf0f5;
  background: #fafbfe;
}

.uploaded-file-item:last-child {
  border-bottom: 0;
}

.uploaded-file-link {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: #5f66ec;
  font-size: 13px;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uploaded-file-link:hover {
  text-decoration: underline;
}

.remove-file-btn {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  color: #e45757;
  cursor: pointer;
  font-size: 13px;
}

.hidden-input {
  display: none;
}

.anchor-card {
  margin-top: 18px;
  padding: 16px 16px 18px;
  border: 1px solid #dfe4f0;
  border-radius: 12px;
  background: #fff;
}

.anchor-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.anchor-card-head strong {
  color: #1e293b;
  font-size: 18px;
}

.remove-anchor-btn {
  border: 0;
  background: transparent;
  color: #f26666;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.add-anchor-btn {
  margin-top: 16px;
  min-width: 110px;
  height: 38px;
  border: 1px dashed #9b8bff;
  border-radius: 8px;
  background: #fff;
  color: #6f68f3;
  cursor: pointer;
}

.confirm-box {
  margin-top: 18px;
  padding: 18px;
  border: 1px solid #efe6ff;
  border-radius: 12px;
  background: rgba(255, 166, 166, 0.04);
}

.confirm-title {
  color: #5f66ec;
  font-size: 18px;
  font-weight: 600;
}

.confirm-box ul {
  margin: 14px 0 0 18px;
  padding: 0;
  color: #6d758c;
  font-size: 14px;
  line-height: 2;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  margin-top: 30px;
}

.footer-actions.split {
  justify-content: space-between;
}

.right-actions {
  display: flex;
  gap: 14px;
}

.footer-btn {
  min-width: 110px;
  height: 40px;
  border-radius: 8px;
  cursor: pointer;
}

.footer-btn.back {
  border: 1px solid #d9dfeb;
  background: #fff;
  color: #6b7280;
}

.footer-btn.ghost {
  border: 1px solid #eac47f;
  background: #fffdf7;
  color: #d4982b;
}

.footer-btn.primary {
  border: 0;
  background: #6f68f3;
  color: #fff;
}

.footer-btn.submit {
  border: 0;
	color: #fff;
	width: 114px;
	height: 40px;
	background: #6F68F3;
	border-radius: 8px 8px 8px 8px;
  color: #fff;
}

@media (max-width: 960px) {
  .form-grid,
  .three-grid,
  .two-grid,
  .dual-inputs {
    grid-template-columns: 1fr;
  }

  .top-grid .wide,
  .anchor-grid .wide-two,
  .estimate-row {
    grid-column: auto;
    max-width: 100%;
  }

  .footer-actions,
  .footer-actions.split,
  .right-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
